import React, { useEffect, useMemo, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import LoadingDNA from '../../components/LoadingDNA';
import { EmptyState, KpiCard, MetaStrip, SectionCard, StatusBadge, TableControls } from '../../components/admin/AdminPrimitives';
import { formatDateIN, normalizeWorkflowStatus, workflowStatusLabel, workflowStatusTone } from '../../utils/adminFormat';
import { EmployeeDirectoryRecord, TripRecord, getCompanyEmployees, getTrips, isApiError } from '../../services/dashboardService';

const AdminTripsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [trips, setTrips] = useState<TripRecord[]>([]);
  const [employeeById, setEmployeeById] = useState<Map<string, EmployeeDirectoryRecord>>(new Map());
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'draft'>('all');

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        const [tripRows, employeeRows] = await Promise.all([getTrips({ perPage: 500 }), getCompanyEmployees()]);
        setTrips(tripRows);
        setEmployeeById(new Map(employeeRows.map((row) => [row.id, row])));
        setLastRefreshedAt(new Date());
      } catch (error) {
        if (isApiError(error)) setErrorMessage(error.message);
        else if (error instanceof Error) setErrorMessage(error.message);
        else setErrorMessage('Failed to load trips.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const pendingCount = useMemo(() => trips.filter((item) => normalizeWorkflowStatus(String(item.status || '')) === 'pending').length, [trips]);
  const approvedCount = useMemo(() => trips.filter((item) => normalizeWorkflowStatus(String(item.status || '')) === 'approved').length, [trips]);
  const rejectedCount = useMemo(() => trips.filter((item) => normalizeWorkflowStatus(String(item.status || '')) === 'rejected').length, [trips]);
  const filteredTrips = useMemo(() => {
    const q = search.trim().toLowerCase();
    return trips.filter((item) => {
      const normalizedStatus = normalizeWorkflowStatus(String(item.status || ''));
      if (statusFilter !== 'all' && normalizedStatus !== statusFilter) return false;
      if (!q) return true;
      const employee = employeeById.get(item.employee_id);
      const haystack = [
        item.id,
        item.destination || '',
        item.purpose || '',
        employee?.name || employee?.email || '',
        employee?.manager_name || '',
        employee?.department || '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [employeeById, search, statusFilter, trips]);

  return (
    <AdminShell
      pageTitle="Trips"
      subtitle="Company-wide trip visibility and workflow status."
    >
      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{errorMessage}</p> : null}

      {!isLoading ? (
        <div className="space-y-4">
          <MetaStrip
            left="Reporting period: current workflow cycle"
            right={`Last refreshed: ${lastRefreshedAt ? lastRefreshedAt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}`}
          />

          <div className="grid gap-3 md:grid-cols-3">
            <KpiCard label="Trips Awaiting Approval" value={String(pendingCount)} tone={pendingCount > 0 ? 'warning' : 'positive'} />
            <KpiCard label="Approved Trips" value={String(approvedCount)} tone="positive" helper="Current workflow cycle" />
            <KpiCard label="Rejected Trips" value={String(rejectedCount)} tone={rejectedCount > 0 ? 'danger' : 'neutral'} helper="Current workflow cycle" />
          </div>

          <SectionCard title="Trip Register" subtitle="Operational register for workflow and ownership.">
            {trips.length > 0 ? (
              <>
                <TableControls
                  searchValue={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search trip ID, destination, employee, manager, department..."
                  filters={(
                    <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                      <select
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value as 'all' | 'pending' | 'approved' | 'rejected' | 'draft')}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-slate-500"
                      >
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="draft">Draft</option>
                      </select>
                    </label>
                  )}
                />

                {filteredTrips.length === 0 ? (
                  <EmptyState compact message="No trips match the selected filters." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1080px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <th className="pb-2">Trip ID</th>
                          <th className="pb-2">Employee</th>
                          <th className="pb-2">Manager</th>
                          <th className="pb-2">Department</th>
                          <th className="pb-2">Destination</th>
                          <th className="pb-2">Purpose</th>
                          <th className="pb-2">Status</th>
                          <th className="pb-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTrips.map((item) => {
                          const employee = employeeById.get(item.employee_id);
                          const employeeName = employee?.name || employee?.email || 'Unknown employee';
                          const managerName = employee?.manager_name || 'Unassigned';
                          const department = employee?.department || 'No department';
                          const status = normalizeWorkflowStatus(String(item.status || ''));

                          return (
                            <tr key={item.id} className="border-b border-slate-100 text-sm text-slate-700">
                              <td className="py-3 font-semibold text-slate-900">{item.id.slice(0, 8)}</td>
                              <td className="py-3">{employeeName}</td>
                              <td className="py-3">{managerName}</td>
                              <td className="py-3">{department}</td>
                              <td className="py-3">{item.destination || '-'}</td>
                              <td className="py-3">{item.purpose || '-'}</td>
                              <td className="py-3"><StatusBadge label={workflowStatusLabel(status)} tone={workflowStatusTone(status)} /></td>
                              <td className="py-3">{formatDateIN(item.start_date || item.created_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm font-semibold text-slate-600">No trips available for this selection.</p>
            )}
          </SectionCard>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminTripsPage;
